import { NavLink } from 'react-router-dom'
import { Users, Baby, Wrench, DollarSign, TrendingUp, Gift, Settings, UserPlus, Sparkles, Package, PhoneCall, type LucideIcon } from 'lucide-react'
import { useAuth, can, type ModuleKey, type StaffUser } from '../auth/AuthContext'
import { useSettingsAccess } from '../../modules/settings/useSettingsAccess'
import styles from './Sidebar.module.css'

// Icon-only sidebar, matching the layout Kate pointed to in the reference
// screenshots (brightwheel-style admin: icon rail + top bar, not a
// horizontal nav). Labels are tooltips, not text, to keep it compact —
// same pattern as the reference apps.
// What a person sees comes from Settings -> Access (user.modules), never from role lists here.
const MODULES: { path: string; label: string; Icon: LucideIcon; modules: ModuleKey[] }[] = [
  // Assistant first (Kate, 2026-10-29, doc 68) — also where "/" lands for everyone who has it
  { path: '/assistant', label: 'Assistant', Icon: Sparkles, modules: ['assistant', 'knowledge'] },
  // Students includes Upcoming enrollment (Kate, 2026-10-16) — open for people with either module
  { path: '/students', label: 'Students', Icon: Baby, modules: ['students', 'students_dashboard', 'students_capacity', 'students_exit_interviews', 'enrollment'] },
  { path: '/staff', label: 'Staff', Icon: Users, modules: ['staff'] },
  { path: '/maintenance', label: 'Maintenance', Icon: Wrench, modules: ['maintenance'] },
  { path: '/inventory', label: 'Inventory', Icon: Package, modules: ['inventory'] },
  { path: '/billing', label: 'Billing', Icon: DollarSign, modules: ['billing_dashboard', 'billing_debts', 'billing_adjustments'] },
  { path: '/marketing', label: 'Marketing', Icon: TrendingUp, modules: ['marketing'] },
  { path: '/sales', label: 'Sales', Icon: PhoneCall, modules: ['sales'] },
  { path: '/recruitment', label: 'Recruitment', Icon: UserPlus, modules: ['recruitment', 'recruitment_spend', 'recruitment_settings'] },
  { path: '/loyalty', label: 'Loyalty', Icon: Gift, modules: ['loyalty_dashboard', 'loyalty_requests', 'loyalty_support', 'loyalty_exit_interviews', 'loyalty_reviews', 'loyalty_video_reviews', 'loyalty_referrals', 'loyalty_events', 'loyalty_settings', 'loyalty_families', 'surveys'] },
]

/** Where "/" should land: the first module this person may open. */
export function firstModulePath(user: StaffUser | null): string {
  return MODULES.find((m) => m.modules.some((k) => can(user, k)))?.path ?? (can(user, 'settings') ? '/settings' : '/students')
}

export default function Sidebar() {
  const { user } = useAuth()
  const settingsAccess = useSettingsAccess(!!user)
  const visible = MODULES.filter((m) => m.modules.some((k) => can(user, k)))
  return (
    <aside className={styles.sidebar}>
      <div className={styles.mark}>LS</div>
      <nav className={styles.nav}>
        {visible.map(({ path, label, Icon }) => (
          <NavLink
            key={path}
            to={path}
            title={label}
            className={({ isActive }) =>
              isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem
            }
          >
            <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
          </NavLink>
        ))}
      </nav>
      <div className={styles.spacer} />
      {(can(user, 'settings') || can(user, 'data_admin', 'edit') || settingsAccess?.can_view) && (
        <NavLink
          to="/settings"
          title="Settings"
          className={({ isActive }) => (isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem)}
        >
          <Settings size={20} strokeWidth={1.75} aria-hidden="true" />
        </NavLink>
      )}
    </aside>
  )
}
